import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Modal, Input, Spin, Empty } from "antd";
import { toast } from "../../components/toasts/toastStore";
import { apiFacturacionInstance, authHeaderFacturacion } from "../../redux/actions/facturacion/facturacion";

import "./EventoDetallePage.css";

dayjs.locale("es");

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY, HH:mm") : "—";
};

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const ACTION_CONFIG = {
  CREATE: { label: "Apertura", color: "#15803d", bg: "#dcfce7" },
  UPDATE: { label: "Actualizacion", color: "#f90336", bg: "#fee2e6" },
};
const getActionConfig = (a) => ACTION_CONFIG[a] || { label: a || "Cambio", color: "#595c5e", bg: "#f1f5f9" };

export default function PeriodoDetallePage() {
  const { idPeriodo } = useParams();
  const navigate = useNavigate();

  const [periodo, setPeriodo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("datos");

  const [cerrarOpen, setCerrarOpen] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [folioFiscal, setFolioFiscal] = useState("");

  const [actividad, setActividad] = useState([]);
  const [loadingActividad, setLoadingActividad] = useState(false);

  const fetchPeriodo = useCallback(() => {
    setLoading(true);
    apiFacturacionInstance.get(`/facturacion/periodos/${idPeriodo}`, { headers: authHeaderFacturacion() })
      .then((res) => setPeriodo(res.data))
      .catch(() => toast.error("No se pudo cargar el periodo"))
      .finally(() => setLoading(false));
  }, [idPeriodo]);

  useEffect(() => { fetchPeriodo(); }, [fetchPeriodo]);

  useEffect(() => {
    if (activeTab !== "actividad") return;
    setLoadingActividad(true);
    apiFacturacionInstance.get(`/facturacion/periodos/${idPeriodo}/actividad`, { headers: authHeaderFacturacion() })
      .then((res) => setActividad(res.data || []))
      .catch(() => {})
      .finally(() => setLoadingActividad(false));
  }, [activeTab, idPeriodo]);

  const handleCerrar = async () => {
    if (!folioFiscal.trim()) return toast.warning("Captura el folio fiscal de la factura global");
    setCerrando(true);
    try {
      await apiFacturacionInstance.post(`/facturacion/periodos/${idPeriodo}/cerrar`, { folio_fiscal: folioFiscal.trim() }, { headers: authHeaderFacturacion() });
      toast.success("Periodo cerrado");
      setCerrarOpen(false);
      setFolioFiscal("");
      fetchPeriodo();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cerrar el periodo");
    } finally {
      setCerrando(false);
    }
  };

  if (loading) {
    return (
      <div className="cd-main" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!periodo) {
    return (
      <div className="cd-main" style={{ padding: 40 }}>
        <Empty description="Periodo no encontrado" />
      </div>
    );
  }

  return (
    <div className="cd-main">
      <div className="cd-content">
        <div className="cd-header-card">
          <div className="cd-header-top">
            <div>
              <div className="cd-header-name-row">
                <h1 className="cd-client-name">PERIODO #{periodo.id_periodo_global}</h1>
              </div>
              <div className="cd-header-meta">
                <span className="cd-meta-item"><i className="fa-solid fa-calendar" /> {periodo.fecha_inicio} — {periodo.fecha_fin}</span>
                <span className="cd-meta-item"><i className="fa-solid fa-user" /> {periodo.creado_por}</span>
              </div>
            </div>
            <div className="cd-header-actions">
              {!periodo.cerrado && (
                <Button type="primary" icon={<i className="fa-solid fa-lock" />} onClick={() => setCerrarOpen(true)} style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}>
                  Cerrar periodo
                </Button>
              )}
            </div>
          </div>

          <div className="cd-header-tabs">
            <button className={`cd-tab-btn ${activeTab === "datos" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("datos")}>
              <i className="fa-solid fa-file-lines" /> Informacion
            </button>
            <button className={`cd-tab-btn ${activeTab === "ventas" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("ventas")}>
              <i className="fa-solid fa-receipt" /> Ventas del periodo ({(periodo.ventas || []).length})
            </button>
            <button className={`cd-tab-btn ${activeTab === "actividad" ? "cd-tab-btn-active" : ""}`} onClick={() => setActiveTab("actividad")}>
              <i className="fa-solid fa-clock" /> Actividad
            </button>
          </div>
        </div>

        {activeTab === "datos" && (
          <div className="cd-bento-grid" style={{ gridTemplateColumns: "2fr 1fr" }}>
            <div className="cd-card cd-card-client">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-file-invoice-dollar"></i></div>
                <h2 className="cd-card-title">Resumen del periodo</h2>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 32px" }}>
                <div><span className="cd-field-label">Ventas incluidas</span><span className="cd-field-value">{(periodo.ventas || []).length}</span></div>
                <div><span className="cd-field-label">Total del periodo</span><span className="cd-field-value">{money(periodo.total_periodo)}</span></div>
              </div>
            </div>

            <div className="cd-card">
              <div className="cd-card-header">
                <div className="cd-card-icon-wrap"><i className="fa-solid fa-info"></i></div>
                <h2 className="cd-card-title">Datos generales</h2>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div><span className="cd-field-label">Estado</span><span className="cd-field-value">{periodo.cerrado ? "Cerrado" : "Abierto"}</span></div>
                <div><span className="cd-field-label">Abrio</span><span className="cd-field-value">{fmtDatetime(periodo.datetime)}</span></div>
                {periodo.cerrado === 1 && <div><span className="cd-field-label">Folio fiscal</span><span className="cd-field-value">{periodo.folio_fiscal || "—"}</span></div>}
              </div>
            </div>
          </div>
        )}

        {activeTab === "ventas" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-receipt"></i></div>
              <h2 className="cd-card-title">Ventas del periodo</h2>
            </div>
            {(periodo.ventas || []).length === 0 ? (
              <Empty description="No hubo ventas en este rango de fechas" />
            ) : (
              <div className="cd-pagos-list">
                {periodo.ventas.map((v) => (
                  <div
                    key={v.id_venta}
                    style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, padding: "12px 4px", borderBottom: "1px solid #f0edf2", cursor: "pointer" }}
                    onClick={() => navigate(`/app/ventas/${v.id_venta}`)}
                  >
                    <div><span className="cd-field-label">Venta</span><div className="cd-field-value">#{v.id_venta}</div></div>
                    <div><span className="cd-field-label">Fecha</span><div className="cd-field-value">{fmtDatetime(v.datetime)}</div></div>
                    <div><span className="cd-field-label">Total</span><div className="cd-field-value">{money(v.total)}</div></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "actividad" && (
          <div className="cd-card">
            <div className="cd-card-header">
              <div className="cd-card-icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h2 className="cd-card-title">Actividad</h2>
              <span className="cd-actividad-count">{actividad.length}</span>
            </div>
            {loadingActividad ? <Spin /> : (
              <div className="cd-actividad-list">
                {actividad.map((item, idx) => {
                  const cfg = getActionConfig(item.action);
                  return (
                    <div key={item.id_audit_log || idx} className="cd-actividad-item">
                      <div className="cd-actividad-dot-col">
                        <div className="cd-actividad-dot" style={{ background: cfg.color }} />
                        {idx < actividad.length - 1 && <div className="cd-actividad-line" />}
                      </div>
                      <div className="cd-actividad-body">
                        <div className="cd-actividad-top-row">
                          <span className="cd-actividad-badge" style={{ color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                          <span className="cd-actividad-message">{item.message}</span>
                        </div>
                        <div className="cd-actividad-bottom-row">
                          <span className="cd-actividad-time">{fmtDatetime(item.datetime)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={cerrarOpen}
        onCancel={() => setCerrarOpen(false)}
        onOk={handleCerrar}
        okText="Cerrar periodo"
        cancelText="Cancelar"
        title="Cerrar periodo de facturacion global"
        confirmLoading={cerrando}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>Captura el folio fiscal de la factura global emitida para este periodo:</div>
          <Input placeholder="Folio fiscal" value={folioFiscal} onChange={(e) => setFolioFiscal(e.target.value)} autoFocus />
        </div>
      </Modal>
    </div>
  );
}
